import React, { useState } from 'react';
import styled from '@emotion/native';
import { TouchableOpacity } from 'react-native';
import { useQuery } from '@apollo/client';
import { H4, BodyText } from '@/shared/components/StyledText';
import { white, mint, green } from '@/shared/styles/colors';
import { GET_CLIENT } from '@/shared/queries';
import { QueryResult, QueryClientArgs } from '@/shared/types/generated';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import Button from '@/shared/components/Button';
import ClientProfileModal from '@/shared/components/ClientProfileModal';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { ActivityIndicator } from 'react-native-paper';

interface ClientInfoBoxProps {
  clientId: number;
  onToggleNoteForm: () => void;
}

const ClientInfoBox: React.FC<ClientInfoBoxProps> = ({
  clientId,
  onToggleNoteForm,
}) => {
  const { isDesktop } = useBreakpoint();
  const { user } = useGetUser();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { navigateToChannel } = useChannelRouting();
  const setSnackbarState = useStore((state) => state.setSnackbarState);

  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);

  const { data, loading } = useQuery<QueryResult<'client'>, QueryClientArgs>(
    GET_CLIENT,
    {
      variables: { input: { id: clientId } },
      skip: !clientId,
      onError: (error) => {
        setSnackbarState({
          open: true,
          message: 'Error fetching client data',
          severity: 'error',
        });
        SentryService.captureError('Failed to get client', error);
      },
    },
  );

  const handleViewChat = async () => {
    if (!user?.id || !data?.client?.user?.id) {
      return;
    }
    const channelUrl = await fetchChannelUrl(user.id, data.client.user.id);
    if (channelUrl) {
      navigateToChannel(channelUrl);
    }
  };

  const handleViewProfile = () => {
    setIsProfileModalVisible(true);
  };

  const client = data?.client;
  const clientName = `${client?.user?.firstName} ${client?.user?.lastName}`;

  if (loading) {
    return (
      <LoadingContainer>
        <ActivityIndicator />
      </LoadingContainer>
    );
  }

  return (
    <>
      <Container isDesktop={isDesktop}>
        <ContentWrapper isDesktop={isDesktop}>
          <ImageSection>
            <ClientImage source={{ uri: client?.user?.profilePicUrl || '' }} />
          </ImageSection>

          <InfoSection>
            <ClientName>{clientName}</ClientName>

            <ButtonContainer>
              <TouchableOpacity onPress={handleViewProfile}>
                <LinkText>View Profile</LinkText>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleViewChat}
                style={{ marginLeft: 16 }}
              >
                <LinkText>View Chat</LinkText>
              </TouchableOpacity>
            </ButtonContainer>
          </InfoSection>
        </ContentWrapper>
        <Button
          text="Add New Note"
          onPress={onToggleNoteForm}
          customStyle={{
            height: isDesktop ? 40 : 47,
            width: isDesktop ? 136 : '100%',
            marginTop: isDesktop ? 0 : 16,
          }}
          customLabelStyle={{ fontSize: 11 }}
        />
      </Container>
      <ClientProfileModal
        isVisible={isProfileModalVisible}
        setIsVisible={setIsProfileModalVisible}
        clientId={clientId}
      />
    </>
  );
};

export default ClientInfoBox;

const Container = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  width: '100%',
  justifyContent: isDesktop ? 'space-between' : 'flex-start',
  alignItems: isDesktop ? 'center' : 'flex-start',
  backgroundColor: white,
  borderRadius: 4,
  borderWidth: 1,
  borderColor: mint,
  padding: 16,
  marginBottom: isDesktop ? 16 : 24,
}));

const LoadingContainer = styled.View({
  justifyContent: 'center',
  alignItems: 'center',
});

const ContentWrapper = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'row',
  alignItems: isDesktop ? 'center' : 'flex-start',
}));

const ImageSection = styled.View({
  marginRight: 16,
  marginBottom: 0,
});

const ClientImage = styled.Image({
  width: 86,
  height: 58,
  borderRadius: 4,
});

const InfoSection = styled.View({
  flex: 1,
});

const ClientName = styled(H4)({
  fontSize: 25,
  letterSpacing: -0.25,
});

const ButtonContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
});

const LinkText = styled(BodyText)({
  color: green,
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 0.12,
  textDecorationLine: 'underline',
});
